<template>
  <div class="listbox-item" ref="listbox-item" :class="listboxItemClass">
    <slot name="prefix">
      <Icon v-if="icon" :name="icon" class="listbox-item-icon" />
    </slot>

    <View class="listbox-item-text" direction="column" justify="center" gap="xs" :grow="1">
      <Text class="listbox-item-title" variant="body-medium" v-if="title">
        {{ title }}
      </Text>

      <Text class="listbox-item-subtitle" variant="body-small" v-if="subtitle">
        {{ subtitle }}
      </Text>
    </View>

    <LoadingIndicator v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue';

import Icon from '../icon.vue';
import LoadingIndicator from '../loading-indicator.vue';
import Text from '../text.vue';
import View from '../view/view.vue';
import { ListboxItemProps } from './listbox-item-props.interface';

const props = withDefaults(defineProps<ListboxItemProps>(), {
  variant: 'normal',
  isLoading: true,
});

const listboxItemRef = useTemplateRef<HTMLDivElement>('listbox-item');

const listboxItemClass = computed(() => [
  {
    active: props.isActive,
    loading: props.isLoading,
  },
  props.variant,
]);

defineExpose({ listboxItemRef });
</script>

<style scoped lang="scss">
.listbox-item,
.listbox-item-text {
  height: 3rem;
}

.listbox-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
  padding-inline: var(--t-spacing-md);

  &:hover,
  &.active {
    background-color: var(--t-color-neutral-200);
    cursor: pointer;
  }

  .listbox-item-icon {
    flex-shrink: 0;
  }

  .listbox-item-subtitle {
    color: var(--t-text-color-secondary);
  }

  &.loading {
    .listbox-item-text,
    :slotted([name='prefix']),
    .listbox-item-icon {
      opacity: 0.5;
    }
  }

  &.dangerous {
    color: var(--t-color-red-400);

    .listbox-item-title,
    .listbox-item-subtitle {
      color: var(--t-color-red-400);
    }
  }

  &.info {
    color: var(--t-color-blue-500);

    .listbox-item-title,
    .listbox-item-subtitle {
      color: var(--t-color-blue-500);
    }
  }
}
</style>
